export const SUPPORTED_INFINITE_SCROLL_COMPONENTS = new Set([
  "ProductGridPage",
  "ProductGridSearch",
  "ProductGridShop",
  "MatrixCardsGrid",
  "OrdersGridPage",
]);

const PAGE_ALIASES = Object.freeze({
  home: "HOME",
  HOME: "HOME",
  ProductCatalog: "DEFAULT",
  newProducts: "newProducts",
  bestSellers: "bestSellers",
  dealTodays: "dealTodays",
  search: "productSearch",
  productSearch: "productSearch",
  shopUser: "SHOP_USER",
  SHOP_USER: "SHOP_USER",
  userView: "userView",
});

export const DEFAULT_INFINITE_SCROLL_SETTINGS = Object.freeze({
  entries: Object.freeze([]),
});

export const DEFAULT_INFINITE_SCROLL_DEVICES = Object.freeze({ desktop: true, tablet: true, mobile: true });

export const getInfiniteScrollViewportDevice = () => {
  if (typeof window === "undefined") return "desktop";
  if (window.innerWidth <= 767) return "mobile";
  if (window.innerWidth <= 1024) return "tablet";
  return "desktop";
};

export const normalizeInfiniteScrollPageId = (pageId) => (
  PAGE_ALIASES[pageId] || pageId || "HOME"
);

export const categoryInfiniteScrollPageId = (categoryId) =>
  `category_${String(categoryId || "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 70)}`;

const hasOwnArray = (value, key) => (
  value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, key) && Array.isArray(value[key])
);

// An own `sections: []` / `facets: []` is an intentional empty page.  An
// absent property means "inherit". This distinction is shared by the public
// category renderer and the settings catalogue.
export const extractConfiguredPage = (configuration) => {
  if (!configuration || typeof configuration !== "object") return null;
  if (hasOwnArray(configuration, "sections")) {
    const sorted = [...configuration.sections].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    const facets = sorted.filter((entry) => entry?.sectionId && entry.enabled !== false).map((entry) => entry.sectionId);
    const configOverrides = {};
    sorted.forEach((entry) => {
      if (!entry?.sectionId) return;
      const override = {};
      if (entry.props && Object.keys(entry.props).length) override.props = entry.props;
      if (entry.overrides && Object.keys(entry.overrides).length) override.overrides = entry.overrides;
      if (Object.keys(override).length) configOverrides[entry.sectionId] = override;
    });
    return { facets, configOverrides, sections: sorted };
  }
  if (hasOwnArray(configuration, "facets")) {
    return { facets: [...configuration.facets], configOverrides: {}, sections: configuration.facets.map((sectionId, order) => ({ sectionId, order })) };
  }
  return null;
};

export const resolveEffectivePageConfig = (...configurations) => {
  for (const configuration of configurations) {
    const resolved = extractConfiguredPage(configuration);
    if (resolved) return resolved;
  }
  return { facets: [], configOverrides: {}, sections: [] };
};

export const normalizeInfiniteScrollSettings = (value) => {
  const entries = Array.isArray(value?.entries) ? value.entries : [];
  const seen = new Set();
  return {
    // Developer authorization of Seller product display (My Shop / Shop
    // Builder: Shop Product Grid or Infinite Scroll). Absent = off.
    sellerProductDisplay: { enabled: value?.sellerProductDisplay?.enabled === true },
    entries: entries.reduce((out, entry) => {
      const pageId = normalizeInfiniteScrollPageId(String(entry?.pageId || "").trim());
      const sectionId = String(entry?.sectionId || "").trim();
      const key = `${pageId}:${sectionId}`;
      if (!pageId || !sectionId || seen.has(key)) return out;
      seen.add(key);
      const titleMode = ["existing", "custom", "hidden"].includes(entry?.titleMode)
        ? entry.titleMode
        : "existing";
      const devices = {
        desktop: entry?.devices?.desktop !== false,
        tablet: entry?.devices?.tablet !== false,
        mobile: entry?.devices?.mobile !== false,
      };
      out.push({
        pageId,
        sectionId,
        enabled: entry?.enabled === true,
        titleMode,
        customTitle: titleMode === "custom" ? String(entry?.customTitle || "").slice(0, 120) : "",
        devices,
      });
      return out;
    }, []),
  };
};

export const getInfiniteScrollEntry = (settings, pageId, sectionId, categoryId = null) => {
  const normalizedPage = normalizeInfiniteScrollPageId(pageId);
  const entries = normalizeInfiniteScrollSettings(settings).entries;
  const scopedPage = categoryId ? categoryInfiniteScrollPageId(categoryId) : null;
  return entries.find((entry) => entry.pageId === scopedPage && entry.sectionId === sectionId)
    || entries.find((entry) => entry.pageId === normalizedPage && entry.sectionId === sectionId)
    || null;
};

export const resolveInfiniteScroll = (allSettings, pageId, sectionId, categoryId = null) => {
  const entry = getInfiniteScrollEntry(allSettings?.infiniteScroll, pageId, sectionId, categoryId);
  const devices = entry?.devices || DEFAULT_INFINITE_SCROLL_DEVICES;
  const viewportDevice = getInfiniteScrollViewportDevice();
  return {
    enabled: entry?.enabled === true && devices[viewportDevice] !== false,
    configured: entry?.enabled === true,
    devices,
    viewportDevice,
    titleMode: entry?.titleMode || "existing",
    customTitle: entry?.customTitle || "",
  };
};

// The settings screen consumes the same page/section registry as the renderer.
// A section is exposed only when it both declares real data pagination and is
// rendered by a component that implements the reusable observer contract.
export const resolveConfiguredSections = (configuration, sections) => {
  const resolved = extractConfiguredPage(configuration);
  if (!resolved) return [];
  return resolved.sections.reduce((out, configured) => {
    if (!configured?.sectionId || configured.enabled === false) return out;
    const base = sections?.[configured.sectionId];
    if (!base) return out;
    const overrides = configured.overrides || {};
    out.push({
      ...base,
      id: configured.sectionId,
      component: overrides.component ?? base.component,
      facetKey: overrides.facetKey ?? configured.props?.facetKey ?? base.facetKey,
      dataKey: overrides.dataKey ?? configured.props?.dataKey ?? base.dataKey,
      props: { ...(base.defaultProps || {}), ...(configured.props || {}) },
    });
    return out;
  }, []);
};

export const buildInfiniteScrollCatalog = (pages, sections, savedPages = {}) => Object.values(pages || {})
  .filter((page) => page?.id && !["DEFAULT", "DEFAULT_ALL"].includes(page.id))
  .map((page) => {
    const effective = resolveEffectivePageConfig(savedPages?.[page.id], page);
    const compatible = resolveConfiguredSections(effective, sections).reduce((out, section) => {
      const sectionId = section.id;
      if (!section?.requiresPagination || !SUPPORTED_INFINITE_SCROLL_COMPONENTS.has(section.component)) return out;
      if (!out.some((item) => item.id === sectionId)) {
        out.push({
          id: sectionId,
          title: section.meta?.title || sectionId,
          component: section.component,
          dataKey: section.dataKey,
          facetKey: section.facetKey,
        });
      }
      return out;
    }, []);
    return { id: page.id, name: page.name || page.id, sections: compatible };
  })
  .filter(Boolean);

const compatibleSections = (configuration, sections) => resolveConfiguredSections(configuration, sections)
  .filter((section) => section.requiresPagination && SUPPORTED_INFINITE_SCROLL_COMPONENTS.has(section.component))
  .map((section) => ({
    id: section.id,
    title: section.meta?.title || section.id,
    component: section.component,
    dataKey: section.dataKey,
    facetKey: section.facetKey,
  }));

export const buildInfiniteScrollCategoryTree = (rootCategory, pages, sections, savedPages = {}, savedCategories = {}) => {
  const visit = (category, depth = 0) => {
    if (!category?._id) return null;
    const templateId = category.PEPCData || "AAB";
    const effective = resolveEffectivePageConfig(savedCategories?.[category._id], category.pageCategory, savedPages?.[templateId], pages?.[templateId]);
    const children = (category.items || [])
      .map((item) => visit(item?.supCategoryID || item, depth + 1))
      .filter(Boolean);
    return {
      id: categoryInfiniteScrollPageId(category._id),
      categoryId: String(category._id),
      name: Array.isArray(category.name) ? category.name[0] : category.name || String(category._id),
      templateId,
      depth,
      sections: compatibleSections(effective, sections),
      children,
    };
  };
  return (rootCategory?.items || []).map((item) => visit(item?.supCategoryID || item)).filter(Boolean);
};

export const mergeUniqueById = (current = [], incoming = []) => {
  const result = [];
  const seen = new Set();
  [...current, ...incoming].forEach((item, index) => {
    const id = item?._id || item?.id || item?.slug || `value:${JSON.stringify(item) || index}`;
    if (seen.has(id)) return;
    seen.add(id);
    result.push(item);
  });
  return result;
};

